import ErrorBoundary from 'components/error-boundaries';
import { useState } from 'react';
import { SectionTitle } from 'modules/marketplace-performance-page/components/section-title';
import {
  StrategyPerformance,
  MonthlyStats,
} from 'api/types/httpsTypes/marketplace-performance';
import ColumnChart from 'modules/marketplace-performance-page/components/bar-chart';
import Table, { Body } from 'modules/shared-components/data-display/table';
import {
  Position,
  riskColumn,
} from 'modules/marketplace-performance-page/components/risk-chart-column';
import PieChart from 'modules/marketplace-performance-page/components/pie-chart';
import {
  DistributionTableData,
  hitsDistributionColumn,
} from 'modules/marketplace-performance-page/components/hits-distribution-chart-column';
import optionColumnsWithNegativeValue from 'modules/marketplace-performance-page/charts/optionColumnsWithNegativeValue';
import optionColumnsTradeFrequency from 'modules/marketplace-performance-page/charts/optionColumnsTradeFrequency';
import optionColumnsHoldingTime from 'modules/marketplace-performance-page/charts/optionColumnsHoldingTime';
import { Select } from 'modules/shared-components/forms/select';
import { PERIODS } from 'modules/marketplace-performance-page/constants/PERIODS';
import InfoBox from 'modules/marketplace-performance-page/components/info-box-overview';
import moment from 'moment';
import Tooltip from 'modules/shared-components/tooltip';
import Big from 'big.js';
import { sortBy } from 'lodash/fp';

interface RiskOrdersTableDataDataProps {
  data: { key?: number; hits: number; fraction: string }[];
}

interface MaxHitOrdersProps {
  data: { key: number; hits: number; fraction: string }[];
}

export function calculateHoldingTime(data: number) {
  if (data === 0) return '0-10m';
  if (data === 10) return '10-30m';
  if (data === 30) return '30m-1h';
  if (data === 60) return '1-2h';
  if (data === 120) return '2-6h';
  if (data === 360) return '6-12h';
  if (data === 720) return '12h-1d';
  if (data === 1440) return '1d-1w';
  if (data === 10080) return '1w-1M';
  else return '>1M';
}

const Overview = ({ monthlyReturns, stats }: StrategyPerformance) => {
  function hitsDistributionTableData(
    data: MonthlyStats['tradesDistribution']['chart'],
    others: MonthlyStats['tradesDistribution']['others'] | undefined
  ): DistributionTableData {
    const list: DistributionTableData = data
      ? data.slice(0, 4).map((hitsDistribution) => {
          return hitsDistribution;
        })
      : [];

    if (list.length === 4 && others) {
      list.push(others);
    }

    return list;
  }

  function riskOrdersTableData(
    data: MonthlyStats['risk']['orders'],
    others: MonthlyStats['risk']['others'] | undefined
  ): RiskOrdersTableDataDataProps['data'] {
    const list: RiskOrdersTableDataDataProps['data'] = data
      ? data.slice(0, 4)
      : [];

    if (list.length === 4 && others) {
      list.push(others);
    }

    return list;
  }

  const periods = PERIODS;
  const [selectedPeriod, setSelectedPeriod] = useState(PERIODS[0].value);

  function onChange(e: 'sevenDays' | 'oneMonth' | 'threeMonths' | 'sixMonths') {
    setSelectedPeriod(e);
  }

  return (
    <ErrorBoundary>
      <div className="space-y-6">
        <SectionTitle>
          Monthly Returns{' '}
          <Tooltip text="The monthly returns are calculated as the gross PnL over a a calendar month divided by the required assets. The required assets are the assets needed to run the bot, specifically the max amount of base/quote asset open at the same time. ">
            <span className="d-inline-block">
              <i className="mdi mdi-information-outline" />
            </span>
          </Tooltip>
        </SectionTitle>
        <div className="">
          <ColumnChart
            options={optionColumnsWithNegativeValue}
            height="300px"
            name="PnL"
            data={
              monthlyReturns?.map((data) => {
                return {
                  x: moment(data.date).format('MM/YY'),
                  y: data.returns,
                };
              }) || []
            }
          />
        </div>

        <div className="lg:flex lg:justify-between lg:items-center">
          <SectionTitle>
            Positions Frequency{' '}
            <Tooltip.Info text="The total number of new positions opened by the bot over a selected period." />
          </SectionTitle>

          <div className="w-full lg:w-40">
            <Select
              options={periods}
              onChange={(e) => onChange(e?.value)}
              value={selectedPeriod}
              disabled={false}
            />
          </div>
        </div>

        <div className="lg:flex space-y-2 lg:space-y-0 lg:space-x-2">
          <div className="w-full lg:w-2/3 flex justify-center items-center bg-white-300 dark:bg-black-700 rounded-md">
            <ColumnChart
              height="300px"
              options={optionColumnsTradeFrequency}
              name="Positions Opened"
              data={
                stats?.[selectedPeriod].tradesFrequency?.chart?.map((data) => {
                  return {
                    x: moment(data.date).format('DD/MM/YY'),
                    y: data.trades,
                  };
                }) || []
              }
            />
          </div>
          <div className="w-full lg:w-1/3 bg-white-300 dark:bg-black-700 flex justify-center items-center rounded-md">
            <div className="w-full xxs:px-4">
              <div className="border-bottom-new border-b-1 xxs:border-b-1 xxs:border-white-600 dark:border-black-400">
                <InfoBox
                  title="New Positions Opened"
                  value={stats?.[selectedPeriod]?.tradesFrequency.totalTrades}
                />
              </div>
              <InfoBox
                title="New Positions Opened per Week"
                value={
                  stats?.[selectedPeriod]?.tradesFrequency.avgTradesPerWeek
                }
              />
            </div>
          </div>
        </div>

        <SectionTitle>
          Trades Distribution{' '}
          <Tooltip text="An overview about the different pairs traded over a period with relative number of hits, distribution over trading volume, and avg. PnL over capital deployed.">
            <span className="d-inline-block">
              <i className="mdi mdi-information-outline" />
            </span>
          </Tooltip>
        </SectionTitle>
        <div className="lg:flex space-x-2">
          <div className="lg:w-3/5 flex justify-center items-center bg-black-700 rounded-md">
            <PieChart
              suffix=""
              labels={
                stats?.[selectedPeriod]?.tradesDistribution?.chart.map(
                  (prop) => {
                    return prop.base + '/' + prop.quote;
                  }
                ) || []
              }
              series={
                stats?.[selectedPeriod]?.tradesDistribution?.chart.map(
                  (prop) => {
                    return prop.hits;
                  }
                ) || []
              }
            />
          </div>
          <div className="lg:w-2/5  flex bg-black-700 grow rounded-md">
            <Table>
              <Table.Header columns={hitsDistributionColumn} />
              <Body
                columns={hitsDistributionColumn}
                data={[
                  ...hitsDistributionTableData(
                    stats?.[selectedPeriod]?.tradesDistribution?.chart || [],
                    stats?.[selectedPeriod]?.tradesDistribution?.others
                  ),
                ].map((distribution, index) => ({
                  ...distribution,
                  id: `${index}`,
                }))}
              />
            </Table>
          </div>
        </div>

        <SectionTitle>
          Holding Time Distribution{' '}
          <Tooltip text="An overview about the holding time over a selected period. The holding time is calculated as the amount of time that a position is held open. For example, if you buy 100 USDT of BTC, and after 1 hour you sell the same amount, then the holding time is 1 hour.  ">
            <span className="d-inline-block">
              <i className="mdi mdi-information-outline" />
            </span>
          </Tooltip>
        </SectionTitle>
        <div className="lg:flex space-y-2 lg:space-y-0 lg:space-x-2">
          <div className="w-full lg:w-2/3 flex justify-center items-center bg-white-300 dark:bg-black-700 rounded-md">
            <ColumnChart
              height="300px"
              options={optionColumnsHoldingTime}
              name="Number of Trades"
              data={
                stats?.[selectedPeriod].holdingTime?.chart.map((data) => {
                  return {
                    x: calculateHoldingTime(data.holdingTime),
                    y: data.positions,
                  };
                }) || []
              }
            />
          </div>
          <div className="w-full lg:w-1/3 bg-white-300 dark:bg-black-700 flex justify-center items-center rounded-md">
            <div className="w-full xxs:px-4">
              <div className="border-bottom-new border-b-1 xxs:border-b-1 xxs:border-white-600 dark:border-black-400">
                <InfoBox
                  title="Avg. Holding time"
                  suffix="hr"
                  value={stats?.[selectedPeriod].holdingTime?.avgHoldingTime}
                />
              </div>
              <InfoBox
                title="Max. holding time"
                suffix="hr"
                value={stats?.[selectedPeriod].holdingTime?.maxHoldingTime}
              />
            </div>
          </div>
        </div>

        {stats?.[selectedPeriod]?.risk?.orders &&
          stats?.[selectedPeriod]?.risk?.orders?.length > 0 && (
            <>
              <SectionTitle>
                Risk{' '}
                <Tooltip text="An overview about the risk of the position. For a DCA bot, the risk is calculated based on how many safety orders have been executed. The more a bot use safety orders, the more risky it is as the chances that the price goes against the first assumption (the base order) is higher. ">
                  <span className="d-inline-block">
                    <i className="mdi mdi-information-outline" />
                  </span>
                </Tooltip>
              </SectionTitle>
              <div className="">
                <div className="text-sm font-medium text-black-900 dark:text-white-50 xxs:mb-1 ">
                  {Big(stats?.[selectedPeriod]?.risk?.winRatio || 0)
                    .mul(100)
                    .toFixed(1)}
                  % Win Ratio,{' '}
                  {Big(
                    maxHitsOrders(stats?.[selectedPeriod]?.risk?.orders || [])
                      .maxFraction
                  ).toFixed(1)}
                  % of the trades closed at{' '}
                  {maxHitsOrders(stats?.[selectedPeriod]?.risk?.orders || [])
                    .maxKey === 0 ? (
                    'Base Order'
                  ) : (
                    <span>
                      Safety Order{' '}
                      {
                        maxHitsOrders(
                          stats?.[selectedPeriod]?.risk?.orders || []
                        ).maxKey
                      }
                    </span>
                  )}
                </div>
                <div className="">
                  <Table>
                    <Table.Header columns={riskColumn} />
                    <Body
                      columns={riskColumn}
                      data={
                        [
                          ...riskOrdersTableData(
                            stats?.[selectedPeriod]?.risk?.orders || [],
                            stats?.[selectedPeriod]?.risk?.others
                          ),
                        ].map((position, index) => ({
                          ...position,
                          id: `${index}`,
                        })) as Position[]
                      }
                    />
                  </Table>
                </div>
              </div>
            </>
          )}
      </div>
    </ErrorBoundary>
  );
};

export function maxHitsOrders(props: MaxHitOrdersProps['data']) {
  if (!props?.length) return { maxFraction: 0, maxKey: 0 };

  const [max] = sortBy((x) => -Big(x.fraction).toNumber(), props);

  return {
    maxFraction: Big(max.fraction).times(100).toNumber(),
    maxKey: max.key,
  };
}

export default Overview;
